import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { TextAreaProps } from '@toasttab/shared/src/interfaces/Interface.d'

const TextArea = ({
  id,
  name,
  label,
  onChange,
  onBlur,
  disabled = false,
  variation = 'default',
  value,
  error,
  required,
  placeholder,
  classes
}: TextAreaProps) => {
  const [hasValue, setHasValue] = useState(false)

  // We need this useEffect call to check the initial input state for the form and apply the correct styling.
  // Sometimes autofill values aren't properly handled on load, which leads to the label overlapping the text.
  // This forces the component to check that value and trigger a rerender of the component to ensures the proper styling.
  useEffect(() => {
    // The !! forces this to a boolean for our state value
    setHasValue(!!value)
  }, [value])

  const labelClassNames = classNames({
    hidden: !label && variation !== 'default' && variation !== 'inverse',
    'font-sansPro font-normal text-left text-secondary tracking-[0.2px] pointer-events-none h-[24px] mb-[3px]':
      true,
    'text-base leading-[20px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'absolute top-[12px] left-[10px] right-[10px] peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'absolute left-[10px] right-[10px] text-[10px] leading-[10px] top-[10px]':
      label && (variation === 'inverse' || variation === 'default') && hasValue
  })

  const inputClassNames = classNames(
    'bg-white text-base placeholder:text-gray-50 text-default rounded-[4px] font-sansPro min-h-[116px]',
    {
      // Full Width
      'w-full': true,
      // Border shadow
      'border border-gray-50 shadow-none text-base box-border peer/input':
        (variation === 'inverse' || variation === 'default') && !error,
      'placeholder-transparent':
        (variation === 'inverse' || variation === 'default') && label !== '',

      // Padding
      'pt-3 pb-9 px-[10px]': true,
      // Focus Padding
      'focus-within:pt-4 focus-within:pb-9': label,
      'pt-4 pb-9': label && hasValue
    }
  )

  const errorClassNames = classNames({
    'absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'default',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-white text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline-inverse before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'inverse'
  })

  return (
    <>
      <textarea
        className={inputClassNames}
        disabled={disabled}
        id={id}
        maxLength={255}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={!label ? placeholder : null}
        required={required}
        value={value}
      />
      {label ? (
        <label className={labelClassNames} htmlFor={id}>
          {label}
        </label>
      ) : null}
      {error ? (
        <div className={errorClassNames} role='alert'>
          {error}
        </div>
      ) : null}
    </>
  )
}

export default TextArea
